import React, { useState, useEffect, useCallback } from 'react';
import {
  Search,
  Trophy,
  UserPlus,
  TrendingUp,
  Calendar,
  MapPin,
  Filter,
  X,
  Users,
  MessageCircle
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { getSportIcon } from '../../utils/sportUtils';


// Define interfaces for type safety
interface SearchUser {
  id: string;
  name: string;
  sport: string;
  role: string;
  avatar?: string;
  isFollowing: boolean;
  hasPendingRequest?: boolean;
  followersCount?: number;
}

interface SearchSport {
  id: string;
  name: string;
  image: string;
  players: number;
  posts: number;
}

interface SearchTournament {
  id: string;
  name: string;
  sport: string;
  location: string;
  date: string;
  image: string;
  participants: number;
}

interface SearchCommunity {
  id: string;
  name: string;
  sport: string;
  description: string;
  image: string;
  members: number;
}

interface SearchResults {
  users: SearchUser[];
  sports: SearchSport[];
  tournaments: SearchTournament[];
  communities: SearchCommunity[];
  total: number;
}

const SearchPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchResults, setSearchResults] = useState<SearchResults>({
    users: [],
    sports: [],
    tournaments: [],
    communities: [],
    total: 0
  });
  const [activeFilter, setActiveFilter] = useState<'all' | 'users' | 'sports' | 'tournaments' | 'communities'>('all');
  const [suggestions, setSuggestions] = useState<SearchUser[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [discoveryUsers, setDiscoveryUsers] = useState<SearchUser[]>([]);
  const [discoverySports, setDiscoverySports] = useState<SearchSport[]>([]);
  const [discoveryTournaments, setDiscoveryTournaments] = useState<SearchTournament[]>([]);

  // Load initial suggestions
  useEffect(() => {
    loadSuggestions();
  }, []);

  // Debounced search
  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      // Allow searching if there is a term, OR if we specifically want to see a filtered list
      if (searchTerm.trim() || activeFilter !== 'all') {
        performSearch(searchTerm);
        setHasSearched(true);
      } else {
        // Clear search results only when no search term and "all" filter
        setSearchResults({
          users: [],
          sports: [],
          tournaments: [],
          communities: [],
          total: 0
        });
        setHasSearched(false);
      }
    }, 300);

    return () => clearTimeout(delayedSearch);
  }, [searchTerm, activeFilter]);

  const loadSuggestions = async () => {
    try {
      // Load suggestions and discovery content
      const [usersRes, sportsRes, tournamentsRes] = await Promise.all([
        api.get('/search?q=&filter=users&limit=8'),
        api.get('/search?q=&filter=sports&limit=8'),
        api.get('/search?q=&filter=tournaments&limit=4')
      ]);

      if (usersRes.data.success && usersRes.data.data.users) {
        setSuggestions(usersRes.data.data.users.slice(0, 3));
        setDiscoveryUsers(usersRes.data.data.users);
      }

      if (sportsRes.data.success && sportsRes.data.data.sports) {
        setDiscoverySports(sportsRes.data.data.sports);
      }

      if (tournamentsRes.data.success && tournamentsRes.data.data.tournaments) {
        setDiscoveryTournaments(tournamentsRes.data.data.tournaments);
      }

      // If no results, add some fallback data
      if (discoverySports.length === 0) {
        setDiscoverySports([
          { id: 'football', name: 'Football', image: 'https://images.unsplash.com/photo-1431324155629-1a6deb1dec8d?w=200&h=120&fit=crop', players: 150, posts: 89 },
          { id: 'cricket', name: 'Cricket', image: 'https://images.unsplash.com/photo-1540747913346-19e32dc3e97e?w=200&h=120&fit=crop', players: 120, posts: 67 },
          { id: 'chess', name: 'Chess', image: 'https://images.unsplash.com/photo-1528819622765-d6bcf132f793?w=200&h=120&fit=crop', players: 85, posts: 45 },
          { id: 'tennis', name: 'Tennis', image: 'https://images.unsplash.com/photo-1554068865-24cecd4e34b8?w=200&h=120&fit=crop', players: 95, posts: 52 }
        ]);
      }
    } catch (error) {
      console.error('Failed to load suggestions:', error);
      // Add fallback data on error
      setDiscoverySports([
        { id: 'football', name: 'Football', image: 'https://images.unsplash.com/photo-1431324155629-1a6deb1dec8d?w=200&h=120&fit=crop', players: 150, posts: 89 },
        { id: 'cricket', name: 'Cricket', image: 'https://images.unsplash.com/photo-1540747913346-19e32dc3e97e?w=200&h=120&fit=crop', players: 120, posts: 67 },
        { id: 'chess', name: 'Chess', image: 'https://images.unsplash.com/photo-1528819622765-d6bcf132f793?w=200&h=120&fit=crop', players: 85, posts: 45 },
        { id: 'tennis', name: 'Tennis', image: 'https://images.unsplash.com/photo-1554068865-24cecd4e34b8?w=200&h=120&fit=crop', players: 95, posts: 52 }
      ]);
    }
  };

  const performSearch = useCallback(async (query: string) => {
    try {
      setLoading(true);
      
      // Real API call
      const response = await api.get(`/search?q=${encodeURIComponent(query)}&filter=${activeFilter}`);
      
      if (response.data.success) {
        setSearchResults(response.data.data);
      } else {
        // Show empty results if API doesn't return success
        setSearchResults({
          users: [],
          sports: [],
          tournaments: [],
          communities: [],
          total: 0
        });
      }
    } catch (error) {
      console.error('Search failed:', error);
      // Show empty results on error
      setSearchResults({
        users: [],
        sports: [],
        tournaments: [],
        communities: [],
        total: 0
      });
    } finally {
      setLoading(false);
    }
  }, [activeFilter]);

  const handleFollow = async (userId: string) => {
    try {
      console.log('Sending follow request to user:', userId);
      
      // Optimistic update - show as request sent
      const updateUserState = (user: SearchUser) => {
        if (user.id === userId) {
          return { ...user, isFollowing: false, hasPendingRequest: true };
        }
        return user;
      };

      setSuggestions(prev => prev.map(updateUserState));
      setDiscoveryUsers(prev => prev.map(updateUserState));
      setSearchResults(prev => ({
        ...prev,
        users: prev.users.map(updateUserState)
      }));

      // Real API call - sends follow request
      const response = await api.post(`/users/${userId}/follow`);
      console.log('Follow request response:', response.data);
      
      if (response.data.success) {
        toast.success('Follow request sent!');
        // Reload suggestions to get updated follow status
        loadSuggestions();
      }
    } catch (error: any) {
      console.error('Failed to send follow request:', error);
      const errorMessage = error.response?.data?.error?.message || 'Failed to send follow request';
      
      // If request already sent, keep the "Request Sent" state, don't revert
      if (errorMessage.includes('already sent') || errorMessage.includes('Follow request already sent')) {
        // Keep the optimistic update - request is already sent
        toast('Follow request already sent', { icon: 'ℹ️' });
        // Reload suggestions to get the correct state from server
        loadSuggestions();
        return;
      }
      
      // For other errors, show error and revert
      toast.error(errorMessage);
      
      // Revert optimistic update on error
      const revertUserState = (user: SearchUser) => {
        if (user.id === userId) {
          return { ...user, isFollowing: false, hasPendingRequest: false };
        }
        return user;
      };

      setSuggestions(prev => prev.map(revertUserState));
      setDiscoveryUsers(prev => prev.map(revertUserState));
      setSearchResults(prev => ({
        ...prev,
        users: prev.users.map(revertUserState)
      }));
    }
  };

  const handleMessage = async (userId: string) => {
    try {
      const response = await api.post('/chats', { participantId: userId });
      if (response.data.success) {
        const chat = response.data.data.chat;
        navigate(`/chats/${chat.id}`);
        toast.success('Chat opened!');
      }
    } catch (error) {
      console.error('Failed to create chat:', error);
      toast.error('Failed to open chat');
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
    setSearchResults({
      users: [],
      sports: [],
      tournaments: [],
      communities: [],
      total: 0
    });
    setHasSearched(false);
  };

  return (
    <div className="max-w-4xl mx-auto p-2 sm:p-4 space-y-4 sm:space-y-6">
      {/* Search Bar */}
      <div className="sticky top-0 bg-white z-10 pb-3 sm:pb-4 -mx-2 sm:-mx-4 px-2 sm:px-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
          <input
            type="text"
            placeholder="Search players, coaches, sports..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 sm:pl-10 pr-10 sm:pr-12 py-2.5 sm:py-3 text-sm sm:text-base bg-gray-100 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white border border-transparent focus:border-blue-200"
          />
          {searchTerm && (
            <button
              onClick={clearSearch}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          )}
        </div>

        {/* Search Filters */}
        {searchTerm && (
          <div className="flex items-center space-x-2 mt-2 sm:mt-3 overflow-x-auto pb-2 scrollbar-hide">
            <button
              className="flex items-center space-x-1 px-2 sm:px-3 py-1 sm:py-1.5 bg-gray-100 rounded-full text-xs sm:text-sm font-medium text-gray-700 hover:bg-gray-200 flex-shrink-0"
            >
              <Filter className="w-3 h-3 sm:w-4 sm:h-4" />
              <span>Filters</span>
            </button>
            {['all', 'users', 'sports', 'tournaments', 'communities'].map((filter) => (
              <button
                key={filter}
                onClick={() => setActiveFilter(filter as any)}
                className={`px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-medium capitalize transition-colors flex-shrink-0 whitespace-nowrap ${
                  activeFilter === filter
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {filter}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Search Results */}
      {hasSearched ? (
        <div className="space-y-6">
          {loading && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
          )}

          {!loading && (
            <>
              {/* Users Results */}
              {(activeFilter === 'all' || activeFilter === 'users') && searchResults.users?.length > 0 && (
                <div>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">
                    Players & Coaches ({searchResults.users.length})
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                    {searchResults.users.map((user: SearchUser) => (
                      <UserCard key={user.id} user={user} onFollow={handleFollow} onMessage={handleMessage} />
                    ))}
                  </div>
                </div>
              )}

              {/* Sports Results */}
              {(activeFilter === 'all' || activeFilter === 'sports') && searchResults.sports?.length > 0 && (
                <div>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">
                    Sports ({searchResults.sports.length})
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-4">
                    {searchResults.sports.map((sport: SearchSport) => (
                      <SportCard key={sport.id} sport={sport} />
                    ))}
                  </div>
                </div>
              )}

              {/* Tournaments Results */}
              {(activeFilter === 'all' || activeFilter === 'tournaments') && searchResults.tournaments?.length > 0 && (
                <div>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">
                    Tournaments & Events ({searchResults.tournaments.length})
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    {searchResults.tournaments.map((tournament: SearchTournament) => (
                      <TournamentCard key={tournament.id} tournament={tournament} />
                    ))}
                  </div>
                </div>
              )}

              {/* Communities Results */}
              {(activeFilter === 'all' || activeFilter === 'communities') && searchResults.communities?.length > 0 && (
                <div>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">
                    Communities ({searchResults.communities.length})
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    {searchResults.communities.map((community: SearchCommunity) => (
                      <CommunityCard key={community.id} community={community} />
                    ))}
                  </div>
                </div>
              )}

              {/* No Results */}
              {!loading && 
               searchResults.users?.length === 0 && 
               searchResults.sports?.length === 0 && 
               searchResults.tournaments?.length === 0 && 
               searchResults.communities?.length === 0 && 
               hasSearched && (
                <div className="text-center py-12">
                  <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No results found</h3>
                  <p className="text-gray-500">
                    Try searching for different keywords or check your spelling
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      ) : (
        /* Default Discovery Content */
        <div className="space-y-6">
          {/* Suggested Users */}
          {suggestions.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-3 sm:mb-4">
                <h2 className="text-base sm:text-lg font-semibold text-gray-900">Suggested for You</h2>
                <button 
                  onClick={() => { setActiveFilter('users'); setSearchTerm(''); }}
                  className="text-blue-600 text-xs sm:text-sm font-medium hover:text-blue-700"
                >
                  View all
                </button>
              </div>
              <div className="flex space-x-3 sm:space-x-4 overflow-x-auto pb-2 scrollbar-hide">
                {suggestions.map((user) => (
                  <div key={user.id} className="flex-shrink-0 w-40 sm:w-48">
                    <UserCard user={user} onFollow={handleFollow} onMessage={handleMessage} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Players & Coaches Section */}
          {discoveryUsers.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-3 sm:mb-4">
                <h2 className="text-base sm:text-lg font-semibold text-gray-900">Players & Coaches</h2>
                <button 
                  onClick={() => { setActiveFilter('users'); setSearchTerm(' '); }}
                  className="text-blue-600 text-xs sm:text-sm font-medium hover:text-blue-700"
                >
                  View more
                </button>
              </div>
              <div className="flex space-x-3 sm:space-x-4 overflow-x-auto pb-2 scrollbar-hide">
                {discoveryUsers.map((user) => (
                  <div key={user.id} className="flex-shrink-0 w-40 sm:w-48">
                    <UserCard user={user} onFollow={handleFollow} onMessage={handleMessage} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Explore Sports Section */}
          {discoverySports.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-3 sm:mb-4">
                <h2 className="text-base sm:text-lg font-semibold text-gray-900">Explore Sports</h2>
                <button 
                  onClick={() => { setActiveFilter('sports'); setSearchTerm(''); }}
                  className="text-blue-600 text-xs sm:text-sm font-medium hover:text-blue-700"
                >
                  View more
                </button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-4">
                {discoverySports.map((sport) => (
                  <SportCard key={sport.id} sport={sport} />
                ))}
              </div>
            </div>
          )}

          {/* Tournaments & Events Section */}
          {discoveryTournaments.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-3 sm:mb-4">
                <h2 className="text-base sm:text-lg font-semibold text-gray-900">Tournaments & Events</h2>
                <button 
                  onClick={() => { setActiveFilter('tournaments'); setSearchTerm(''); }}
                  className="text-blue-600 text-xs sm:text-sm font-medium hover:text-blue-700"
                >
                  View more
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                {discoveryTournaments.map((tournament) => (
                  <TournamentCard key={tournament.id} tournament={tournament} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
// User Card Component
const UserCard: React.FC<{ user: SearchUser; onFollow: (id: string) => void; onMessage?: (id: string) => void }> = ({ user, onFollow, onMessage }) => {
  const getSportBadgeColor = (sport: string) => {
    const colors = {
      'Chess': 'bg-yellow-100 text-yellow-800',
      'Cricket': 'bg-red-100 text-red-800',
      'Badminton': 'bg-yellow-100 text-yellow-800',
      'Football': 'bg-blue-100 text-blue-800',
      'Tennis': 'bg-green-100 text-green-800'
    };
    return colors[sport as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  return (
    <div className="bg-white rounded-lg p-3 sm:p-4 border border-gray-200 hover:shadow-md transition-shadow">
      <div className="flex items-center space-x-2 sm:space-x-3 mb-2 sm:mb-3">
        <div className="relative">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center bg-blue-500 text-white font-medium text-xs sm:text-sm flex-shrink-0 overflow-hidden border border-gray-100">
            {user.avatar ? (
              <img
                src={user.avatar}
                alt={user.name}
                className="w-full h-full rounded-full object-cover"
              />
            ) : (
              getInitials(user.name)
            )}
          </div>
          {user.sport && user.sport !== 'General' && (
            <div className="absolute -bottom-1 -right-1 bg-white rounded-full shadow-md flex items-center justify-center border-2 border-white w-6 h-6 text-sm z-10">
              {getSportIcon(user.sport)}
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-gray-900 text-xs sm:text-sm truncate">{user.name}</h3>
          <div className="flex items-center space-x-1 mt-0.5 sm:mt-1">
            <span className={`px-1.5 sm:px-2 py-0.5 text-xs font-medium rounded-full ${getSportBadgeColor(user.sport)} whitespace-nowrap`}>
              {user.sport}
            </span>
          </div>
          <p className="text-xs text-gray-500 mt-0.5 sm:mt-1">{user.role}</p>
        </div>
      </div>
      <div className="flex space-x-2">
        <button
          onClick={() => onFollow(user.id)}
          disabled={user.hasPendingRequest}
          className={`flex-1 py-1.5 sm:py-2 px-2 sm:px-3 rounded-lg text-xs sm:text-sm font-medium transition-colors flex items-center justify-center space-x-1 ${
            user.hasPendingRequest
              ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
              : user.isFollowing
              ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              : 'bg-gray-900 text-white hover:bg-gray-800'
          }`}
        >
          <UserPlus className="w-3 h-3 sm:w-4 sm:h-4" />
          <span className="hidden sm:inline">
            {user.hasPendingRequest ? 'Request Sent' : user.isFollowing ? 'Following' : 'Follow'}
          </span>
          <span className="sm:hidden">
            {user.hasPendingRequest ? '⏳' : user.isFollowing ? '✓' : '+'}
          </span>
        </button>
        {onMessage && (
          <button
            onClick={() => onMessage(user.id)}
            className="px-2 sm:px-3 py-1.5 sm:py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            <MessageCircle className="w-3 h-3 sm:w-4 sm:h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

// Sport Card Component
const SportCard: React.FC<{ sport: SearchSport }> = ({ sport }) => {
  return (
    <div className="bg-white rounded-lg overflow-hidden border border-gray-200 hover:shadow-md transition-shadow cursor-pointer">
      <div className="relative">
        <img
          src={sport.image}
          alt={sport.name}
          className="w-full h-20 sm:h-24 object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
      </div>
      <div className="p-2 sm:p-3">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-medium text-gray-900 text-xs sm:text-sm truncate flex-1">{sport.name}</h3>
          <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4 text-green-500 flex-shrink-0 ml-1" />
        </div>
        <p className="text-xs text-gray-500">
          {sport.players} players | {sport.posts} posts
        </p>
      </div>
    </div>
  );
};

// Tournament Card Component
const TournamentCard: React.FC<{ tournament: SearchTournament }> = ({ tournament }) => {
  const getSportBadgeColor = (sport: string) => {
    const colors = {
      'Chess': 'bg-yellow-100 text-yellow-800',
      'Cricket': 'bg-red-100 text-red-800',
      'Badminton': 'bg-yellow-100 text-yellow-800',
      'Football': 'bg-blue-100 text-blue-800',
      'Tennis': 'bg-green-100 text-green-800'
    };
    return colors[sport as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="bg-white rounded-lg overflow-hidden border border-gray-200 hover:shadow-md transition-shadow cursor-pointer">
      <div className="relative">
        <img
          src={tournament.image}
          alt={tournament.name}
          className="w-full h-24 sm:h-32 object-cover"
        />
        <div className="absolute top-2 left-2">
          <span className={`px-1.5 sm:px-2 py-0.5 sm:py-1 text-xs font-medium rounded-full ${getSportBadgeColor(tournament.sport)}`}>
            {tournament.sport}
          </span>
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
      </div>
      <div className="p-3 sm:p-4">
        <h3 className="font-medium text-gray-900 mb-2 text-sm sm:text-base line-clamp-2">{tournament.name}</h3>
        <div className="space-y-1">
          <div className="flex items-center space-x-1 text-xs sm:text-sm text-gray-500">
            <Calendar className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
            <span className="truncate">{new Date(tournament.date).toLocaleDateString()}</span>
          </div>
          {tournament.location && (
            <div className="flex items-center space-x-1 text-xs sm:text-sm text-gray-500">
              <MapPin className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
              <span className="truncate">{tournament.location}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Community Card Component
const CommunityCard: React.FC<{ community: SearchCommunity }> = ({ community }) => {
  const getSportBadgeColor = (sport: string) => {
    const colors = {
      'Football': 'bg-blue-100 text-blue-800',
      'Cricket': 'bg-green-100 text-green-800',
      'Chess': 'bg-purple-100 text-purple-800',
      'Tennis': 'bg-yellow-100 text-yellow-800',
      'Basketball': 'bg-orange-100 text-orange-800',
      'Swimming': 'bg-cyan-100 text-cyan-800',
      'Badminton': 'bg-pink-100 text-pink-800',
      'Athletics': 'bg-red-100 text-red-800',
    };
    return colors[sport as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="bg-white rounded-lg overflow-hidden border border-gray-200 hover:shadow-md transition-shadow cursor-pointer">
      <div className="aspect-video bg-gray-200 relative">
        <img 
          src={community.image} 
          alt={community.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute top-2 left-2">
          <span className={`px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs font-medium ${getSportBadgeColor(community.sport)}`}>
            {community.sport}
          </span>
        </div>
      </div>
      <div className="p-3 sm:p-4">
        <h4 className="font-semibold text-gray-900 mb-1 sm:mb-2 text-sm sm:text-base">{community.name}</h4>
        <p className="text-xs sm:text-sm text-gray-600 mb-2 sm:mb-3 line-clamp-2">{community.description}</p>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-1 text-xs sm:text-sm text-gray-500">
            <Users className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
            <span className="truncate">{community.members} members</span>
          </div>
          <button className="px-2 sm:px-3 py-1 bg-blue-500 text-white text-xs sm:text-sm rounded-lg hover:bg-blue-600 transition-colors whitespace-nowrap">
            Join
          </button>
        </div>
      </div>
    </div>
  );
};

export default SearchPage;